import { defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  CreateFolderRequestSchema,
  FolderGrantSchema,
  FolderSchema,
  KnowledgeBaseFileSchema,
  KnowledgeBaseListingSchema,
  MoveFileRequestSchema,
  MoveFolderRequestSchema,
  SetFolderGrantsRequestSchema,
  StorageSchema,
  UpdateFolderRequestSchema,
  WorkspaceUserSchema,
} from './schemas.js';

export type Folder = z.infer<typeof FolderSchema>;
export type KnowledgeBaseFile = z.infer<typeof KnowledgeBaseFileSchema>;
export type KnowledgeBaseListing = z.infer<typeof KnowledgeBaseListingSchema>;
export type Storage = z.infer<typeof StorageSchema>;

export type WorkspaceUser = z.infer<typeof WorkspaceUserSchema>;
export type FolderGrant = z.infer<typeof FolderGrantSchema>;
export type SetFolderGrantsRequest = z.infer<
  typeof SetFolderGrantsRequestSchema
>;

export const getKnowledgeBaseStorage = defaultClient.query({
  url: 'ai-assistant/knowledge-base/storage' as const,
  method: 'GET',
  responseSchema: StorageSchema,
});

export const getKnowledgeBaseListing = defaultClient.query({
  url: 'ai-assistant/knowledge-base' as const,
  method: 'GET',
  responseSchema: KnowledgeBaseListingSchema,
  querySchema: z.object({ folderId: z.string().nullish() }),
  snakeCaseQueryParams: true,
});

export const getFolder = defaultClient.query({
  url: 'ai-assistant/folders/$folderId' as const,
  method: 'GET',
  responseSchema: FolderSchema,
});

export const createFolder = defaultClient.mutation({
  url: 'ai-assistant/folders' as const,
  method: 'POST',
  requestSchema: CreateFolderRequestSchema,
  responseSchema: FolderSchema,
  onSuccess: (queryClient) => {
    void getKnowledgeBaseListing.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const updateFolder = defaultClient.mutation({
  url: 'ai-assistant/folders/$folderId' as const,
  method: 'PATCH',
  requestSchema: UpdateFolderRequestSchema,
  responseSchema: FolderSchema,
  onSuccess: (queryClient, folder) => {
    void getKnowledgeBaseListing.invalidateAll(queryClient, {});
    void getFolder.invalidate(queryClient, {
      urlParams: { folderId: folder.folderId },
    });
  },
  processResponse(data) {
    return data;
  },
});

export const deleteFolder = defaultClient.mutation({
  url: 'ai-assistant/folders/$folderId' as const,
  method: 'DELETE',
  responseSchema: z.unknown(),
  onSuccess: (queryClient) => {
    void getKnowledgeBaseListing.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const moveFolder = defaultClient.mutation({
  url: 'ai-assistant/folders/$folderId/move' as const,
  method: 'PATCH',
  requestSchema: MoveFolderRequestSchema,
  responseSchema: FolderSchema,
  onSuccess: (queryClient, folder) => {
    void getKnowledgeBaseListing.invalidateAll(queryClient, {});
    void getFolder.invalidate(queryClient, {
      urlParams: { folderId: folder.folderId },
    });
  },
  processResponse(data) {
    return data;
  },
});

export const moveFile = defaultClient.mutation({
  url: 'ai-assistant/files/$fileId/move' as const,
  method: 'PATCH',
  requestSchema: MoveFileRequestSchema,
  responseSchema: z.unknown(),
  onSuccess: (queryClient) => {
    void getKnowledgeBaseListing.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const setFolderGrants = defaultClient.mutation({
  url: 'ai-assistant/folders/$folderId/grants' as const,
  method: 'POST',
  requestSchema: SetFolderGrantsRequestSchema,
  responseSchema: z.array(FolderGrantSchema),
  onSuccess: (queryClient, _grants, { urlParams }) => {
    void getKnowledgeBaseListing.invalidateAll(queryClient, {});
    void getFolder.invalidate(queryClient, { urlParams });
  },
  processResponse(data) {
    return data;
  },
});

export const listWorkspaceUsers = defaultClient.query({
  url: 'ai-assistant/workspace/users' as const,
  method: 'GET',
  responseSchema: z.array(WorkspaceUserSchema),
});
